import React, { useState, useCallback, useEffect } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import { BsSearch } from 'react-icons/bs';
import Button from 'components/inputs/Button';
import Switch from 'components/inputs/Switch';
import { FiEdit2 } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';
import { Row, Col, Modal } from 'react-bootstrap';
import * as _ from 'lodash';
import moment from 'moment';
import SearchBox from 'components/general/SearchBox';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from 'components/general/Pagination';
import { startsWith } from 'components/common/utils';
import { DELETE_WHATSNEW, FETCH_WHATSNEW, GET_WHATSNEW, UPDATE_WHATSNEW } from 'actions/whatsNew';
import DeleteModal from 'components/general/DeleteModal';
import WhatsNewForm from './WhatsNewForm';

const fmt = (d) => (d ? moment(d).format('DD MMM YYYY') : '-');

const WhatsNewList = () => {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [allData, setAllData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  const page = useSelector((state) => state.whatsNew.page);
  const totalData = useSelector((state) => state.whatsNew.total);
  const limit = useSelector((state) => state.whatsNew.limit);
  const list = useSelector((state) => state.whatsNew.whatsNewList);

  const deleteItem = useCallback((id) => dispatch({ type: DELETE_WHATSNEW.REQUEST, payload: id }), []);
  const getList = useCallback((data = {}) => dispatch({ type: FETCH_WHATSNEW.REQUEST, payload: data }), []);
  const getDetail = useCallback((id) => dispatch({ type: GET_WHATSNEW.REQUEST, payload: id }), []);
  const toggleActive = useCallback(
    (id, isActive) => dispatch({ type: UPDATE_WHATSNEW.REQUEST, payload: { id, is_active: isActive } }),
    []
  );

  useEffect(() => {
    getList();
  }, []);

  // CDB-1341 — a story now carries a LIST of app versions (`release_versions`).
  // Stories created before that change still carry the single `release_version`,
  // so both are read here: otherwise new stories show a blank Version column and
  // cannot be found by search.
  const versionsOf = (item) => {
    const list = _.get(item, 'release_versions', []);
    if (!_.isEmpty(list)) return list;
    const single = _.get(item, 'release_version', '');
    return single ? [single] : [];
  };

  // In Individual mode the window lives on each slide, not on the story, so the
  // story-level dates are empty. Show the overall span the story is live for —
  // earliest slide start to latest slide end — rather than a row of dashes.
  const dateRangeOf = (item) => {
    if (_.get(item, 'mode') === 'individual') {
      const starts = _.compact(_.map(_.get(item, 'slides', []), 'start_date'));
      const ends = _.compact(_.map(_.get(item, 'slides', []), 'end_date'));
      if (_.isEmpty(starts) && _.isEmpty(ends)) return '-';
      return `${fmt(_.min(starts))} - ${fmt(_.max(ends))} (per slide)`;
    }
    return `${fmt(_.get(item, 'start_date'))} - ${fmt(_.get(item, 'end_date'))}`;
  };

  useEffect(() => {
    setAllData(_.filter(list, (item) => _.some(versionsOf(item), (v) => startsWith(v, searchText))));
  }, [searchText, list]);

  const handlePageClick = (pageVal) => getList({ page: pageVal });

  const handleClose = () => {
    setShowForm(false);
    setIsEdit(false);
    // Re-fetch so the list is ordered by the server (newest first). The create
    // reducer appends the new record to the end of the array and the update
    // reducer edits it in place, so without this a story just added appears at
    // the bottom and an edited one keeps its old position.
    getList();
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId('');
  };

  // Confirm before deleting so a record is never removed on an accidental click.
  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteItem(deleteId);
    }
    handleCloseDeleteModal();
  };

  return (
    <>
      <LayoutContainer>
        <AdminHeader title="What's New" />
        <div className="page-content-wrapper scrollable">
          <div className="faqList-page">
            <Card>
              <div className="faqList-page-wrapper">
                <Row className="faqList-search-box">
                  <Col xl={10} lg={9} md={8} className="faqList-search-inputBox">
                    <SearchBox value={searchText} preIcon={<BsSearch />} onChange={(e) => setSearchText(e.target.value)} />
                  </Col>
                  <Col xl={2} lg={3} md={4} className="faqList-search-boxButton">
                    <Button
                      className="faqList-search-box-Btn"
                      onClick={() => {
                        setShowForm(true);
                        setIsEdit(false);
                      }}
                    >
                      Add What&apos;s New
                    </Button>
                  </Col>
                </Row>
                <div className="faqList-table">
                  <div className="table-responsive">
                    <table className="record-list-table">
                      <thead>
                        <tr>
                          <th>App Versions</th>
                          <th>Type</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Date Range</th>
                          <th>Slides</th>
                          <th className="action-col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {_.isEmpty(allData) ? (
                          <tr>
                            <td colSpan={7} className="border-0">
                              <div className="empty-data-block">No What&apos;s New found</div>
                            </td>
                          </tr>
                        ) : (
                          _.map(allData, (item, key) => (
                            <tr key={`whatsNew-${key}`}>
                              <td>{_.join(versionsOf(item), ', ') || '-'}</td>
                              {/* Stories created before CDB-1341 have no mode; they behave as Group. */}
                              <td>{_.get(item, 'mode') === 'individual' ? 'Individual' : 'Group'}</td>
                              {/* Shown so an admin can see which priorities are already taken for a
                                  version — equal priorities are allowed and fall back to oldest first. */}
                              <td>{_.get(item, 'priority') != null ? _.get(item, 'priority') : '-'}</td>
                              <td>
                                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                  <Switch
                                    checked={!!_.get(item, 'is_active')}
                                    onChange={(checked) => toggleActive(_.get(item, 'id', ''), checked)}
                                  />
                                  <span>{_.get(item, 'is_active') ? 'Active' : 'Inactive'}</span>
                                </div>
                              </td>
                              <td>{dateRangeOf(item)}</td>
                              <td>{_.size(_.get(item, 'slides', []))}</td>
                              <td>
                                <span
                                  className="faqList-table-editIcon"
                                  onClick={() => {
                                    setShowForm(true);
                                    getDetail(_.get(item, 'id', ''));
                                    setIsEdit(true);
                                  }}
                                >
                                  <FiEdit2 title="Edit" />
                                </span>
                                <span
                                  className="faqList-table-deleteIcon"
                                  onClick={() => {
                                    setDeleteId(_.get(item, 'id', ''));
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  <RiDeleteBinLine title="Delete" />
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {!_.isEmpty(allData) && (
                  <Pagination currentPage={page} limit={limit} total={totalData} onPageChange={(pageVal) => handlePageClick(pageVal)} />
                )}
              </div>
            </Card>
          </div>
        </div>
        {showForm && (
          <Modal show={showForm} size="xl" centered>
            <WhatsNewForm onClose={handleClose} isEdit={isEdit} />
          </Modal>
        )}
        {showDeleteModal && (
          <Modal show={showDeleteModal} centered onHide={handleCloseDeleteModal}>
            <DeleteModal title="What's New" onClose={handleCloseDeleteModal} onRemove={handleConfirmDelete} />
          </Modal>
        )}
      </LayoutContainer>
    </>
  );
};

export default WhatsNewList;
